/* eslint-disable @atlaskit/design-system/use-tokens-typography */
/* eslint-disable @repo/internal/react/require-jsdoc */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useContext } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import { css, jsx, type SerializedStyles } from '@emotion/react';

import { token } from '@atlaskit/tokens';

import HeadingContext from '../heading-context';
import { sentenceToKebab } from '../utils/sentence-to-kebab';

const rootStyles = css({
	margin: 0,
	fontFamily: token('font.family.heading'),
});

const sizeMap: {
	// H6 WPL
	readonly xsmall: SerializedStyles;
	// H5 WPL
	readonly small: SerializedStyles;
	// H4 WPL
	readonly medium: SerializedStyles;
	// H3 WPL
	readonly large: SerializedStyles;
	// H2 WPL
	readonly xlarge: SerializedStyles;
	// H1 small WPL
	readonly xxlarge: SerializedStyles;
	// H1 large WPL (with custom font-weight)
	readonly hero: SerializedStyles;
	/**
	 * Homepage heading sizes
	 */
	// Small article titles on homepage
	readonly smallHome: SerializedStyles;
	// Hero article titles on homepage
	readonly mediumHome: SerializedStyles;
	// Section titles on homepage
	readonly largeHome: SerializedStyles;
	// Hero on homepage
	readonly homeHero: SerializedStyles;
} = {
	// H6 WPL
	xsmall: css({
		fontSize: '1rem', // 16px
		fontWeight: '700',
		lineHeight: '1.25rem', // 20px
	}),
	// H5 WPL
	small: css({
		fontSize: '1.125rem', // 18px
		fontWeight: '700',
		lineHeight: '1.375rem', // 22px
		'@media (min-width: 30rem)': {
			fontSize: '1.125rem', // 18px
			fontWeight: '700',
			lineHeight: '1.375rem', // 22px
		},
		'@media (min-width: 48rem)': {
			fontSize: '1.25rem', // 20px
			fontWeight: '700',
			lineHeight: '1.5rem', // 24px
		},
	}),
	// H4 WPL
	medium: css({
		fontSize: '1.25rem', // 20px
		fontWeight: '700',
		lineHeight: '1.5rem', // 24px
		'@media (min-width: 30rem)': {
			fontSize: '1.375rem', // 22px
			fontWeight: '700',
			lineHeight: '1.75rem', // 28px
		},
		'@media (min-width: 48rem)': {
			fontSize: '1.5rem', // 24px
			fontWeight: '700',
			lineHeight: '1.75rem', // 28px
		},
	}),
	// H3 WPL
	large: css({
		fontSize: '1.5rem', // 24px
		fontWeight: '700',
		lineHeight: '1.75rem', // 28px
		'@media (min-width: 30rem)': {
			fontSize: '1.75rem', // 28px
			fontWeight: '700',
			lineHeight: '2.125rem', // 34px
		},
		'@media (min-width: 48rem)': {
			fontSize: '2rem', // 32px
			fontWeight: '700',
			lineHeight: '2.25rem', // 36px
		},
	}),
	// H2 WPL
	xlarge: css({
		fontSize: '2rem', // 32px
		fontWeight: '700',
		lineHeight: '2.25rem', // 36px
		'@media (min-width: 30rem)': {
			fontSize: '2.25rem', // 36px
			fontWeight: '700',
			lineHeight: '2.5rem', // 40px
		},
		'@media (min-width: 48rem)': {
			fontSize: '2.5rem', // 40px
			fontWeight: '700',
			lineHeight: '2.75rem', // 44px
		},
	}),
	// H1 small WPL
	xxlarge: css({
		fontSize: '2.5rem', // 40px
		fontWeight: '700',
		lineHeight: '2.75rem', // 44px
		'@media (min-width: 30rem)': {
			fontSize: '2.75rem', // 44px
			fontWeight: '700',
			lineHeight: '3rem', // 48px
		},
		'@media (min-width: 48rem)': {
			fontSize: '3rem', // 48px
			fontWeight: '700',
			lineHeight: '3.25rem', // 52px
		},
	}),
	// H1 large WPL (with custom font-weight)
	hero: css({
		fontSize: '3rem', // 48px
		fontWeight: '700',
		lineHeight: '3.25rem', // 52px
		'@media (min-width: 30rem)': {
			fontSize: '3.25rem', // 52px
			fontWeight: '700',
			lineHeight: '3.5rem', // 56px
		},
		'@media (min-width: 48rem)': {
			fontSize: '4.25rem', // 68px
			fontWeight: '700',
			lineHeight: '4.75rem', // 76px
		},
	}),

	/**
	 * Homepage heading sizes
	 */

	// Small article titles on homepage
	smallHome: css({
		font: token('font.heading.small'),
		'@media (min-width: 48rem)': {
			font: token('font.heading.medium'),
		},
	}),

	// Hero article titles on homepage
	mediumHome: css({
		font: token('font.heading.medium'),
		'@media (min-width: 48rem)': {
			font: token('font.heading.xxlarge'),
		},
		'@media (min-width: 64rem)': {
			fontSize: '2.75rem', // 44px
			fontWeight: '700',
			lineHeight: '3rem', // 48px
		},
	}),

	// Section titles on homepage
	largeHome: css({
		fontSize: '2.5rem', // 40px
		fontWeight: '700',
		lineHeight: '2.75rem', // 44px
		'@media (min-width: 30rem)': {
			fontSize: '3rem', // 48px
			lineHeight: '3.25rem', // 52px
		},
		'@media (min-width: 48rem)': {
			fontSize: '4.25rem', // 68px
			lineHeight: '4.50rem', // 72px
		},
	}),

	// Hero on homepage
	homeHero: css({
		fontSize: '4.25rem', // 68px
		fontWeight: '700',
		lineHeight: '4.50rem', // 76px
		'@media (min-width: 30rem)': {
			fontSize: '5.5rem', // 88px - slightly smaller than 1.2x scale to avoid wrap
			lineHeight: '5.75rem', // 92px
		},
		'@media (min-width: 64rem)': {
			fontSize: '7rem', // 112px
			lineHeight: '7.25rem', // 116px
		},
	}),
} as const;

const colorMap = {
	'color.text': css({ color: token('color.text') }),
	'color.text.inverse': css({ color: token('color.text.inverse') }),
};

const depthMap = {
	h1: 1,
	h2: 2,
	h3: 3,
	h4: 4,
	h5: 5,
	h6: 6,
};

interface BrandHeadingProps {
	/**
	 * The label for the heading, used if children have wrapping elements
	 */
	label?: string;
	size: keyof typeof sizeMap;
	as: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
	id?: string;
	color?: 'color.text' | 'color.text.inverse';
	children: ReactNode;
}
export const BrandHeading: ({
	label,
	size,
	as,
	id,
	color,
	children,
}: BrandHeadingProps) => React.JSX.Element = ({
	label,
	size,
	as,
	id,
	color = 'color.text',
	children,
}: BrandHeadingProps): React.JSX.Element => {
	const { addHeading } = useContext(HeadingContext);
	const value = label || children?.toString() || '';
	const computedId = id || sentenceToKebab(label || value);
	const depth = depthMap[as];
	if (depth > 1 && depth <= 3) {
		addHeading({
			id: computedId,
			depth,
			value,
		});
	}

	const Component = as;
	const sizeStyles = sizeMap[size];
	const colorStyles = colorMap[color];
	return (
		<Component id={computedId} css={[rootStyles, sizeStyles, colorStyles]}>
			{children}
		</Component>
	);
};
